import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import { useConfirm } from 'material-ui-confirm';

import OverlaySpinner from 'components/OverlaySpinner';
import {
  deleteProjectMethodology,
  openManageProjectMethodologiesDialog,
  updateProjectMethodology,
} from 'redux/actions/staffingActions';
import { useStaffingSelector } from 'redux/selectors/staffing/staffingSelectors';
import { selectProjectMethodologies } from 'redux/selectors/staffing/getProject';

const useStyles = makeStyles(theme => ({
  paperWidthSm: {
    maxWidth: 760,
  },
  // Position the overlay spinner over this dialog only (not the whole app).
  paper: {
    position: 'relative',
    overflow: 'hidden',
  },
  contentRoot: {
    padding: theme.spacing(5),
  },
  dialogHeader: {
    position: 'relative',
    maxWidth: 760,
    width: 760,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogHeaderRoot: {
    padding: '24px 8px',
  },
  dialogHeaderTitle: {
    fontSize: 16,
    fontWeight: 500,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
  },
  list: {
    maxHeight: 420,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  title: {
    flex: 1,
    fontSize: 14,
    paddingRight: theme.spacing(2),
    wordBreak: 'break-word',
  },
  titleInput: {
    flex: 1,
    marginRight: theme.spacing(2),
    background: 'rgba(0, 0, 0, 0.04)',
    paddingLeft: 5,
    paddingRight: 5,
  },
  emptyState: {
    fontSize: 16,
    textAlign: 'center',
    padding: theme.spacing(3, 0),
  },
}));

/**
 * One methodology row: idle text + pencil/delete, or inline input + floppy while editing.
 *
 * @param {{ id: string, title: string, isEditing: boolean, onStartEdit: Function, onCancelEdit: Function, onSave: Function, onDelete: Function, isSaving: boolean }} props
 */
const MethodologyRow = ({
  id,
  title,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  isSaving,
}) => {
  const classes = useStyles();
  const [draft, setDraft] = useState(title);

  // Keep the input in sync when Firestore pushes a new title (or when this row leaves edit mode).
  useEffect(() => {
    if (!isEditing) {
      setDraft(title);
    }
  }, [isEditing, title]);

  const trimmed = (draft || '').trim();
  const canSave = trimmed.length >= 4 && !isSaving;

  /**
   * Persist the draft title. The parent stays in edit mode when the thunk returns false (409).
   */
  const handleSave = () => {
    if (!canSave) {
      return;
    }
    // Unchanged titles (including whitespace-only edits after trim) do not need a PATCH.
    if (trimmed === title) {
      onCancelEdit();
      return;
    }
    onSave(id, trimmed);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancelEdit();
    }
  };

  return (
    <div className={classes.row}>
      {isEditing ? (
        <Input
          className={classes.titleInput}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          disableUnderline
          autoFocus
          disabled={isSaving}
          inputProps={{ 'aria-label': `Edit ${title}` }}
        />
      ) : (
        <Typography className={classes.title}>{title}</Typography>
      )}

      <IconButton
        size="small"
        aria-label={isEditing ? `Save ${title}` : `Edit ${title}`}
        onClick={isEditing ? handleSave : () => onStartEdit(id)}
        disabled={isEditing ? !canSave : false}
      >
        {isEditing ? <SaveIcon /> : <EditIcon />}
      </IconButton>
      <IconButton size="small" aria-label={`Delete ${title}`} onClick={() => onDelete(id, title)}>
        <DeleteIcon />
      </IconButton>
    </div>
  );
};

/**
 * Staffing admin dialog to rename/delete project methodologies. List data comes from the
 * Firestore `projectMethodologies` listener started in Staffing.js — not the paginated API list.
 */
const ManageProjectMethodologiesDialog = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { isOpenManageProjectMethodologiesDialog } = useStaffingSelector();
  const getAllProjectMethodologies = selectProjectMethodologies();
  const { projectMethodologies, isLoaded } = useSelector(state =>
    getAllProjectMethodologies(state),
  );

  const [editingId, setEditingId] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  const closeDialog = () => {
    if (isBusy) {
      return;
    }
    setEditingId(null);
    dispatch(openManageProjectMethodologiesDialog(false));
  };

  /**
   * PATCH the title. Stay in edit mode when the thunk reports failure (duplicate name, network).
   *
   * @param {string} methodologyId
   * @param {string} title
   */
  const handleSave = (methodologyId, title) => {
    setIsBusy(true);
    Promise.resolve(dispatch(updateProjectMethodology(methodologyId, { title })))
      .then(didSave => {
        if (didSave) {
          setEditingId(null);
        }
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  /**
   * Confirm, then DELETE. The trash control is never disabled for in-use methodologies —
   * the API returns 409 and the thunk snackbars the reason.
   *
   * @param {string} methodologyId
   * @param {string} title
   */
  const handleDelete = (methodologyId, title) => {
    confirm({
      description: `You are about to delete ${title}. This action is permanent.`,
    }).then(() => {
      setIsBusy(true);
      Promise.resolve(dispatch(deleteProjectMethodology(methodologyId)))
        .then(didDelete => {
          if (didDelete && editingId === methodologyId) {
            setEditingId(null);
          }
        })
        .finally(() => {
          setIsBusy(false);
        });
    });
  };

  return (
    <Dialog
      open={Boolean(isOpenManageProjectMethodologiesDialog)}
      onClose={closeDialog}
      aria-labelledby="manage-project-methodologies-title"
      maxWidth="sm"
      classes={{
        paper: classes.paper,
        paperWidthSm: classes.paperWidthSm,
      }}
      disableBackdropClick
      disableEscapeKeyDown
      disableEnforceFocus
    >
      <DialogTitle
        disableTypography
        className={classes.dialogHeader}
        classes={{ root: classes.dialogHeaderRoot }}
      >
        <Typography id="manage-project-methodologies-title" className={classes.dialogHeaderTitle}>
          Manage Methodologies
        </Typography>
        <IconButton
          className={classes.closeButton}
          aria-label="close"
          onClick={closeDialog}
          disabled={isBusy}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.contentRoot}>
        {!isLoaded ? null : !projectMethodologies.length ? (
          <Typography className={classes.emptyState}>No methodologies</Typography>
        ) : (
          <Box className={classes.list}>
            {projectMethodologies.map(methodology => (
              <MethodologyRow
                key={methodology.value}
                id={methodology.value}
                title={methodology.name}
                isEditing={editingId === methodology.value}
                isSaving={isBusy}
                onStartEdit={setEditingId}
                onCancelEdit={() => setEditingId(null)}
                onSave={handleSave}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      {isBusy && <OverlaySpinner />}
    </Dialog>
  );
};

export default ManageProjectMethodologiesDialog;
